import { convertStringToTimezone, StateSelectField, TimezoneField, useForm } from "@tellescope/forms";
import {
  TellescopeModelFromName,
  useAuth,
  useCachedTags,
  useTellescopeMutation,
  useTellescopeQuery,
} from "@tellescope/tanstack-query";
import {
  Button,
  Checkbox,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Input,
  Label,
  MultiSelect,
  PhoneInput,
  Textarea,
  withKey,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

export interface LocationFormDialogContentsProps {
  location?: TellescopeModelFromName<"appointment_locations">;
  locationId?: string;
  onSave?: () => void;
  onCancel?: () => void;
}

export const LocationFormDialogContents = withKey(
  ({ location: providedLocation, locationId, onSave, onCancel }: LocationFormDialogContentsProps) => {
    const { t } = useTranslation("calendar");
    const { selectedOrganization } = useAuth();
    const locationQuery = useTellescopeQuery({
      model: "appointment_locations",
      method: "getOne",
      request: locationId ?? "",
      enabled: !providedLocation && !!locationId,
    });
    const location = providedLocation ?? locationQuery.data;
    const { data: availableTags = [] } = useCachedTags({
      model: "appointment_locations",
    });

    const createLocationMutation = useTellescopeMutation({
      model: "appointment_locations",
      method: "createOne",
    });
    const updateLocationMutation = useTellescopeMutation({
      model: "appointment_locations",
      method: "updateOne",
    });

    const form = useForm({
      fields: {
        title: {
          type: "text",
          initialValue: location?.title,
          required: true,
        },
        timezone: {
          type: "timezone",
          initialValue: location?.timezone,
        },
        phone: {
          type: "phone",
          initialValue: location?.phone,
        },
        address: {
          type: "text",
          initialValue: location?.address,
        },
        city: {
          type: "text",
          initialValue: location?.city,
        },
        state: {
          type: "state",
          initialValue: location?.state,
        },
        zipCode: {
          type: "zipCode",
          initialValue: location?.zipCode,
        },
        instructions: {
          type: "textarea",
          initialValue: location?.instructions,
        },
        tags: {
          type: "multiselect",
          initialValue: location?.tags,
        },
        healthieContactType: {
          type: "text",
          initialValue: location?.healthieContactType,
        },
        healthieUseZoom: {
          type: "checkbox",
          initialValue: location?.healthieUseZoom,
        },
        healthieLocationId: {
          type: "text",
          initialValue: location?.healthieLocationId,
        },
        canvasLocationId: {
          type: "text",
          initialValue: location?.canvasLocationId,
        },
        canvasUseHostDefaultLocation: {
          type: "checkbox",
          initialValue: location?.canvasUseHostDefaultLocation,
        },
      },
      onSubmit: async (values) => {
        if (location) {
          await updateLocationMutation.mutateAsync({
            id: location.id,
            ...values,
            timezone: values.timezone ? convertStringToTimezone(values.timezone) : undefined,
            _options: {
              replaceObjectFields: true,
            },
          });
        } else {
          await createLocationMutation.mutateAsync({
            ...values,
            timezone: values.timezone ? convertStringToTimezone(values.timezone) : undefined,
          });
        }

        onSave?.();
      },
    });

    return (
      <DialogContent divided data-testid="location-form-dialog">
        <DialogHeader>{location ? t("Edit Location") : t("Add Location")}</DialogHeader>
        <form {...form.getFormProps()} className="space-y-6 pb-6 px-6 pt-3">
          <div className="space-y-2">
            <Label htmlFor="title" required>
              {t("Name")}
            </Label>
            <Input
              {...form.getFieldProps("title")}
              id="title"
              placeholder={t("e.g. Main Clinic")}
              data-testid="location-form-title-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="timezone">{t("Timezone")}</Label>
            <span className="flex items-center w-full gap-1">
              <TimezoneField
                value={form.values.timezone ?? ""}
                onChange={(val) => form.setFieldValue("timezone", val)}
                id="timezone"
                placeholder={t("Select a timezone...")}
                wrapperClassName="grow"
                className="h-auto py-1"
              />
              {!!form.values.timezone && (
                <Button variant="ghost" size="sm-icon" onClick={() => form.setFieldValue("timezone", undefined)}>
                  <X />
                </Button>
              )}
            </span>
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">{t("Phone Number")}</Label>
            <PhoneInput {...form.getCustomFieldProps("phone")} id="phone" placeholder={t("(415) 555-0100")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="address">{t("Address")}</Label>
            <Input {...form.getFieldProps("address")} id="address" placeholder={t("Street address")} />
          </div>
          <span className="grid grid-cols-3 gap-2">
            <div className="space-y-2">
              <Label htmlFor="city">{t("City")}</Label>
              <Input {...form.getFieldProps("city")} id="city" placeholder={t("City")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state">{t("State")}</Label>
              <StateSelectField {...form.getFieldProps("city")} id="city" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="zipCode">{t("Zip")}</Label>
              <Input {...form.getFieldProps("zipCode")} id="zipCode" placeholder={t("94103")} />
            </div>
          </span>
          <div className="space-y-2">
            <Label htmlFor="instructions">{t("Arrival Instructions")}</Label>
            <Textarea
              {...form.getFieldProps("instructions")}
              id="instructions"
              placeholder={t("e.g. Enter through the main lobby...")}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tags">{t("Tags")}</Label>
            <MultiSelect
              className="h-auto py-0 w-full"
              value={form.values.tags ?? []}
              onValueChange={(tags) => form.setFieldValue("tags", tags)}
              options={Array.from(new Set([...availableTags, ...(form.values.tags ?? [])])).map((tag) => ({
                label: tag,
                value: tag,
              }))}
              creator={{
                render: (inputValue) => <p>{t("Add '{{tag}}' tag", { tag: inputValue })}</p>,
                transform: (inputValue) => inputValue,
                onCreate: (val) =>
                  form.setFieldValue("tags", Array.from(new Set([...(form.values.tags ?? []), val.value]))),
              }}
            />
          </div>
          {!!selectedOrganization?.hasConnectedHealthie && (
            <>
              <div className="space-y-2">
                <Label htmlFor="healthieContactType">{t("Healthie Contact Type")}</Label>
                <Input
                  {...form.getFieldProps("healthieContactType")}
                  id="healthieContactType"
                  placeholder={t("Contact type")}
                />
                <span className="flex items-center gap-2 pt-2">
                  <Checkbox
                    checked={form.values.healthieUseZoom}
                    onCheckedChange={(checked) => form.setFieldValue("healthieUseZoom", !!checked)}
                    id="healthieUseZoom"
                  />
                  <Label htmlFor="healthieUseZoom" className="cursor-pointer">
                    {t("Use Zoom in Healthie")}
                  </Label>
                </span>
              </div>
              <div className="space-y-2">
                <Label htmlFor="healthieLocationId">{t("Healthie Location ID")}</Label>
                <Input
                  {...form.getFieldProps("healthieLocationId")}
                  id="healthieLocationId"
                  placeholder={t("Location ID")}
                />
              </div>
            </>
          )}
          {!!selectedOrganization?.hasConnectedCanvas && (
            <div className="space-y-2">
              <Label htmlFor="canvasLocationId">{t("Canvas Location ID")}</Label>
              <Input {...form.getFieldProps("canvasLocationId")} id="canvasLocationId" placeholder={t("Location ID")} />
              <span className="flex items-center gap-2 pt-2">
                <Checkbox
                  checked={form.values.canvasUseHostDefaultLocation}
                  onCheckedChange={(checked) => form.setFieldValue("canvasUseHostDefaultLocation", !!checked)}
                  id="canvasUseHostDefaultLocation"
                />
                <Label htmlFor="canvasUseHostDefaultLocation" className="cursor-pointer">
                  {t("Use Host Default Canvas Location ID")}
                </Label>
              </span>
            </div>
          )}
        </form>
        <DialogFooter className="bg-muted rounded-b-md">
          <Button variant="outline" onClick={onCancel} data-testid="location-form-cancel">
            {t("Cancel")}
          </Button>
          <Button
            onClick={() => form.submitForm()}
            className="font-light"
            disabled={location ? !form.isDirty && !form.isValid : !form.isValid}
            loading={updateLocationMutation.isPending || createLocationMutation.isPending}
            data-testid="location-form-submit"
          >
            {location ? t("Save Changes") : t("Add Location")}
          </Button>
        </DialogFooter>
      </DialogContent>
    );
  },
  ({ location, locationId }) => location?.id ?? locationId ?? ""
);
